"use client";

import React, { useState, useEffect } from "react";
import LABELS from "@/components/global-constant/src/general";
import { Phone, Mail } from "lucide-react";
import { HEADER_V2_DESIGN_SYSTEM } from "./designTokens";
import { useFreeShippingThreshold } from "@/packages/hooks/useFreeShippingThreshold";
import { useGeneralSettings } from "@/packages/context/GeneralSettingContext";
import { useCurrency } from "@/packages/context/CurrencyContext";

export default function AnnouncementBarV2() {
  const { colors, typography, spacing } = HEADER_V2_DESIGN_SYSTEM;
  const freeShippingThreshold = useFreeShippingThreshold();
  const { symbol, code } = useCurrency();
  const { settings } = useGeneralSettings();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const supportPhone = (mounted && settings?.contact?.phone) || LABELS.SUPPORT_PHONE;
  const supportEmail = (mounted && settings?.contact?.email) || LABELS.SUPPORT_EMAIL;

  const currSymbol = mounted ? (symbol || "$") : "$";
  const currCode = mounted ? (code?.toUpperCase() || "USD") : "USD";

  const p5000 = `${currSymbol}${(5000).toLocaleString()} ${currCode}`;
  const pShip = (mounted && freeShippingThreshold !== null)
    ? `${currSymbol}${Number(freeShippingThreshold).toLocaleString()} ${currCode}`
    : "";

  const offerText = `🎉 Save 5% on all orders above ${p5000}${pShip ? `    •    🚚 Free shipping on orders over ${pShip}` : ""}`;

  return (
    <div
      role="region"
      aria-label="Announcement Bar"
      className={`${colors.primaryBg} text-white ${typography.fontFamilySans} ${typography.size12} font-medium ${spacing.paddingYxs} w-full border-b ${colors.borderColor}`}
    >
      <div className={`${spacing.containerPadding} max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center`}>
        {/* Left Column: Phone Support */}
        <div className={`hidden md:flex items-center ${spacing.gapXs} text-white/90 min-w-[130px]`}>
          <Phone className={`w-3.5 h-3.5 ${colors.accentText}`} aria-hidden="true" />
          <a
            href={`tel:${supportPhone}`}
            aria-label={`Call customer support at ${supportPhone}`}
            className={`${colors.accentHover} transition-colors duration-200`}
          >
            {supportPhone}
          </a>
        </div>

        {/* Center Column: Marketing Offer (Scrolling Marquee) */}
        <div className="flex-1 w-full md:max-w-[850px] mx-2 overflow-hidden whitespace-nowrap py-1">
          <div className="animate-marquee inline-flex gap-28 hover:[animation-play-state:paused] cursor-pointer text-center tracking-wide uppercase text-white/100 text-[10px] md:text-[11px] font-bold">
            <span>{offerText}</span>
            <span>{offerText}</span>
          </div>
        </div>

        {/* Right Column: Email Support */}
        <div className={`hidden md:flex items-center ${spacing.gapXs} text-white/90 min-w-[160px] justify-end`}>
          <Mail className={`w-3.5 h-3.5 ${colors.accentText}`} aria-hidden="true" />
          <a
            href={`mailto:${supportEmail}`}
            aria-label={`Email customer support at ${supportEmail}`}
            className={`${colors.accentHover} transition-colors duration-200`}
          >
            {supportEmail}
          </a>
        </div>
      </div>
    </div>
  );
}
